/**
 * Shared utilities for matching and pairs management (row/list helpers, no JSX).
 */

import type { CSSProperties } from 'react';
import { Mars, Venus, HelpCircle } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import {
  ASSIGNABILITY_CONFIG,
  DISSOCIATION_REASONS,
  GENDER_LABELS,
  MATCHING_CSV_HEADERS_STUDENT,
  MATCHING_CSV_HEADERS_TUTOR,
  MATCHING_PERSPECTIVE_LABELS,
  MATCHING_SLOT_PILL_CLASS,
  MATCHING_SLOT_PILL_STYLE,
  MATCHING_STATUS_CONFIG,
  SLOT_DAY_NAMES,
  STUDENT_GENDER_LABELS,
} from '@/components/pairs/pair-configs';
import { CSV_MIME } from '@/constants/mime-types.constants';
import { getAssignabilityConfig, getProgramDisplayName } from '@/utils/common-utils';
import {
  TUTOR_VALORISATION_FILTER_OPTIONS,
  type TutorValorisationFilter,
} from '@/components/tutors/tutor-configs';

export {
  formatSlotLabel,
  getAssignabilityConfig,
  getProgramDisplayName,
  getRemoteSubjectLabel,
} from '@/utils/common-utils';

/** Tutor vs student dashboard view. */
export type MatchingPerspective = 'tutor' | 'student';

/** Badge config used by matching gender. */
export interface BadgeConfig {
  label: string;
  icon: typeof Mars | typeof Venus | typeof HelpCircle;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Dashboard row payload from either tutor or student API. */
export type MatchingDashboardItem = TutorDashboardItem | StudentDashboardItem;

/** Unmatched candidate from the opposite side of the current perspective. */
export type MatchingCandidate = UnmatchedStudentItem | UnmatchedTutorItem;

/** Slot check result from the verify-availability API (`taken` / `affectable`). */
export type SlotVerifyResult = { taken: boolean; affectable: boolean };

/** Dashboard item with a stable {@link DataTable} `id` (`userId`). */
export type MatchingDashboardRow = MatchingDashboardItem & { id: string };

/** Pagination, status, and optional CSV export for matching-dashboard queries. */
type MatchingDashboardQueryOptions = {
  matchingStatus: string | undefined;
  assignability: boolean | undefined;
  page?: number;
  pageSize?: number;
  exportCsv?: boolean;
};

import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as matchingService from '@/services/remote/matching.service';
import { useAuthStore } from '@/stores/authStore';
import type {
  AvailabilitySlot,
  CheckAvailabilityRequest,
  CheckAvailabilityResponse,
  DashboardResponse,
  MatchedStudentItem,
  MatchedTutorItem,
  StudentDashboardItem,
  StudentDashboardParams,
  StudentMatchedTutorParams,
  StudentUnmatchedTutorsParams,
  TutorDashboardItem,
  TutorDashboardParams,
  TutorMatchedStudentsParams,
  TutorUnmatchedStudentsParams,
  UnmatchedStudentItem,
  UnmatchedTutorItem,
} from '@/types/remote/matching.types';
import type { Program } from '@/types/program.types';
import type {
  DissociatePairDto,
  DissociationReasonCode,
  UpdatePairManagerDto,
} from '@/types/remote/pair.types';
import type { TutorFilters } from '@/components/tutors/remote/TutorFilters';
import type { StudentFilters } from '@/components/students/remote/StudentFilters';
import { getErrorMessage } from '@/utils/errors-utils';

/**
 * Badge config for matching gender display.
 * Student perspective uses Garçon/Fille, tutor perspective uses Homme/Femme.
 */
export function getMatchingGenderConfig(
  gender: string | null | undefined,
  perspective: MatchingPerspective,
): BadgeConfig {
  const labels = perspective === 'student' ? STUDENT_GENDER_LABELS : GENDER_LABELS;

  if (gender === 'male') return { label: labels.male, icon: Mars, badgeVariant: 'male' };
  if (gender === 'female') return { label: labels.female, icon: Venus, badgeVariant: 'female' };
  if (gender === 'other') {
    return {
      label: labels.other,
      icon: HelpCircle,
      badgeVariant: 'outlined',
      badgeColor: 'rgb(var(--status-neutral))',
    };
  }
  return { label: '—', icon: HelpCircle, badgeVariant: 'ghost' };
}

/** Pick the tutor or student branch of a perspective pair. */
export function pickPerspective<TTutor, TStudent>(
  perspective: MatchingPerspective,
  tutorValue: TTutor,
  studentValue: TStudent,
): TTutor | TStudent {
  if (perspective === 'tutor') return tutorValue;
  return studentValue;
}

/** Tutor table has an extra capacity column (9 vs 8). */
export function matchingTableColSpan(perspective: MatchingPerspective): number {
  return perspective === 'tutor' ? 9 : 8;
}

/** UI copy (title, empty state, create tooltip) for the current perspective. */
export function matchingPerspectiveLabels(perspective: MatchingPerspective) {
  return MATCHING_PERSPECTIVE_LABELS[perspective];
}

/** Empty string/array/null do not count as an active filter. */
export function isActiveFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/** Status, assignability, or any non-empty profile filter keeps Reset enabled. */
export function hasActiveMatchingFilters(
  matchingStatus: string | undefined,
  assignability: boolean | undefined,
  currentFilters: Record<string, unknown>,
): boolean {
  if (matchingStatus) return true;
  if (assignability !== undefined) return true;
  return Object.values(currentFilters).some(isActiveFilterValue);
}

/** Student perspective has no `partially-matched` status — drop it on switch. */
export function matchingStatusAfterPerspectiveChange(
  next: MatchingPerspective,
  matchingStatus: string | undefined,
): string | undefined {
  if (next === 'student' && matchingStatus === 'partially-matched') return undefined;
  return matchingStatus;
}

/** Coordinators do not see the program filter in student mode. */
export function shouldShowMatchingProgramFilter(
  visible: boolean,
  isCoordinator: boolean,
  mode: MatchingPerspective | undefined,
): boolean {
  return visible && !(isCoordinator && mode === 'student');
}

/** Drop programs whose name contains "test". */
export function mapMatchingProgramOptions(
  programs: Array<{ id: string; name?: string }> | undefined,
): Array<{ value: string; label: string }> {
  return (programs ?? [])
    .filter((program) => !program.name?.toLowerCase().includes('test'))
    .map((program) => ({ value: program.id, label: program.name ?? program.id }));
}

/** Student candidates use `studentUserId`; tutors use `tutorUserId`. */
export function getCandidateId(candidate: MatchingCandidate): string {
  return 'studentUserId' in candidate ? candidate.studentUserId : candidate.tutorUserId;
}

/** Student candidates use `studentFullName`; tutors use `tutorFullName`. */
export function getCandidateDisplayName(candidate: MatchingCandidate): string {
  return 'studentFullName' in candidate ? candidate.studentFullName : candidate.tutorFullName;
}

/** Lookup by {@link getCandidateId}; null when the list is empty or the id is missing. */
export function findCandidateById(
  candidates: MatchingCandidate[] | undefined,
  id: string,
): MatchingCandidate | null {
  return candidates?.find((candidate) => getCandidateId(candidate) === id) ?? null;
}

/** True when at least one candidate is not already in the drop zone. */
export function hasVisibleCandidates(candidates: MatchingCandidate[], addedIds: string[]): boolean {
  return candidates.some((candidate) => !addedIds.includes(getCandidateId(candidate)));
}

/** Hide candidates already added to the drop zone. */
export function filterVisibleCandidates(
  candidates: MatchingCandidate[],
  addedIds: string[],
): MatchingCandidate[] {
  return candidates.filter((candidate) => !addedIds.includes(getCandidateId(candidate)));
}

/** Empty-state copy: pick a slot first, else no opposite-side candidates. */
export function getEmptyCandidatesMessage(
  hasSlot: boolean,
  perspective: MatchingPerspective,
): string {
  if (!hasSlot) return 'Sélectionnez un créneau';
  if (perspective === 'tutor') return 'Aucun élève disponible';
  return 'Aucun tuteur disponible';
}

/** Drop-zone copy: drag hint vs no selection. */
export function getDropZoneEmptyMessage(
  showDropHint: boolean,
  perspective: MatchingPerspective,
): string {
  if (showDropHint) return 'Glissez ici';
  if (perspective === 'tutor') return 'Aucun élève sélectionné';
  return 'Aucun tuteur sélectionné';
}

/** Tutor engagement value from the dashboard row; students have none. */
export function getTutorDashboardValorisation(
  data: MatchingDashboardItem,
): TutorValorisationFilter {
  if ('valorisation' in data && data.valorisation) return data.valorisation;
  return 'dont-know';
}

/** Badge config for one tutor valorisation filter value. */
export type TutorValorisationBadge = (typeof TUTOR_VALORISATION_FILTER_OPTIONS)[number];

/** Resolves the matching-row badge for a tutor valorisation value. */
export function getTutorValorisationBadge(
  value: TutorValorisationFilter = 'dont-know',
): TutorValorisationBadge {
  return (
    TUTOR_VALORISATION_FILTER_OPTIONS.find((option) => option.value === value) ??
    TUTOR_VALORISATION_FILTER_OPTIONS[2]
  );
}

/** Tutors expose `subjectToTeach`; students expose `subject`. */
export function getDashboardRawSubject(data: MatchingDashboardItem): string | null {
  if ('subjectToTeach' in data) return data.subjectToTeach || null;
  if ('subject' in data) return data.subject || null;
  return null;
}

/** Tutors use `levels`; students use `level`. Drop entries without a name. */
export function getDashboardNamedLevels(
  data: MatchingDashboardItem,
): Array<{ levelId: string; levelName: string }> {
  const raw = 'levels' in data ? (data.levels ?? []) : (data.level ?? []);
  return raw.filter(
    (level): level is { levelId: string; levelName: string } => level.levelName != null,
  );
}

/** Remaining seats (`leftNumberOfStudentsToSupport`); students have none. */
export function getTutorRemainingCapacity(data: MatchingDashboardItem): number | null {
  if ('leftNumberOfStudentsToSupport' in data) return data.leftNumberOfStudentsToSupport;
  return null;
}

/** 0 = error, 1 = warning, more = success. */
export function getTutorCapacityColor(left: number | null): string {
  if (left === 0) return 'rgb(var(--color-error))';
  if (left !== null && left <= 1) return 'rgb(var(--status-warning))';
  return 'rgb(var(--status-success))';
}

/** Tutors: active / max; students: active / 1. */
export function getMatchingCapacityLabel(
  data: MatchingDashboardItem,
  perspective: MatchingPerspective,
): string {
  if (perspective === 'tutor' && 'numberOfStudentsToSupport' in data) {
    return `${data.activeBinomesCount} / ${data.numberOfStudentsToSupport}`;
  }
  return `${data.activeBinomesCount} / 1`;
}

/** Tutor remaining seats, or student "has a pair" (0 = success). */
export function getMatchingRowCapacityColor(
  data: MatchingDashboardItem,
  perspective: MatchingPerspective,
): string {
  if (perspective === 'tutor' && 'leftNumberOfStudentsToSupport' in data) {
    return getTutorCapacityColor(data.leftNumberOfStudentsToSupport);
  }
  return data.activeBinomesCount === 0 ? 'rgb(var(--status-success))' : 'rgb(var(--color-error))';
}

/** Badge/display fields for one matching table row. */
export function deriveMatchingRowDisplay(
  data: MatchingDashboardItem,
  perspective: MatchingPerspective,
  programsList: Program[],
) {
  return {
    genderConfig: getMatchingGenderConfig(data.gender, perspective),
    subjectValue: getDashboardRawSubject(data),
    capacityLabel: getMatchingCapacityLabel(data, perspective),
    capacityColor: getMatchingRowCapacityColor(data, perspective),
    assignabilityConfig: getAssignabilityConfig(data.affectability),
    partnerName: data.partnerName ?? null,
    valorisation: getTutorDashboardValorisation(data),
    programItems: data.programs.map((program) => ({
      ...program,
      displayName: getProgramDisplayName(program, programsList),
    })),
    levelItems: getDashboardNamedLevels(data),
    statusConfig: MATCHING_STATUS_CONFIG[data.matchingStatus],
  };
}

/** Day of week then start time. */
export function sortAvailabilitySlots(slots: AvailabilitySlot[]): AvailabilitySlot[] {
  return [...slots].sort((a, b) => {
    if (a.dayOfTheWeek !== b.dayOfTheWeek) return a.dayOfTheWeek - b.dayOfTheWeek;
    return (a.startTime ?? '').localeCompare(b.startTime ?? '');
  });
}

/** Next occurrence of an ISO weekday (7 = Sunday). Same weekday next week, not today. */
function getNextWeekdayDate(isoDayOfWeek: number): string {
  const today = new Date();
  const todayJs = today.getDay();
  const targetJs = isoDayOfWeek === 7 ? 0 : isoDayOfWeek;
  const daysAhead = (targetJs - todayJs + 7) % 7 || 7;
  const result = new Date(today);
  result.setDate(today.getDate() + daysAhead);
  return `${result.getFullYear()}-${String(result.getMonth() + 1).padStart(2, '0')}-${String(result.getDate()).padStart(2, '0')}`;
}

/** `HH:mm` → `Hh` or `Hhmm` for the slot-verify API. */
function formatApiSlotTime(time: string): string {
  const [hours, minutes] = (time ?? '').split(':');
  return minutes === '00' ? `${hours}h` : `${hours}h${minutes}`;
}

/** API `{ day, slot }` from a dashboard slot (`fullName` day wins over `dayOfTheWeek`). */
function toApiSlot(slot: {
  fullName?: string;
  startTime: string;
  endTime: string;
  dayOfTheWeek?: number;
}): { day: string; slot: string } {
  const fromFullName = (slot.fullName ?? '').split(' ')[0]?.toLowerCase() ?? '';
  const day =
    fromFullName || (slot.dayOfTheWeek != null ? (SLOT_DAY_NAMES[slot.dayOfTheWeek] ?? '') : '');
  return {
    day,
    slot: `${formatApiSlotTime(slot.startTime)}-${formatApiSlotTime(slot.endTime)}`,
  };
}

/** Map key: `day-start-end` from {@link toApiSlot}. */
export function slotApiKey(slot: {
  fullName?: string;
  startTime: string;
  endTime: string;
  dayOfTheWeek?: number;
}): string {
  const { day, slot: range } = toApiSlot(slot);
  return `${day}-${range}`;
}

/** Index verify results by `${day}-${slot}`. */
export function buildSlotVerifyMap(
  results: Array<{ day: string; slot: string; taken: boolean; affectable: boolean }>,
): Map<string, SlotVerifyResult> {
  const verifyMap = new Map<string, SlotVerifyResult>();
  for (const result of results) {
    verifyMap.set(`${result.day}-${result.slot}`, {
      taken: result.taken,
      affectable: result.affectable,
    });
  }
  return verifyMap;
}

/** First slot that is free and assignable in the verify map. */
export function findFirstFreeSlot(
  availability: AvailabilitySlot[],
  verifyMap: Map<string, SlotVerifyResult>,
): AvailabilitySlot | undefined {
  return availability.find((slot) => {
    const result = verifyMap.get(slotApiKey(slot));
    return result?.taken === false && result.affectable === true;
  });
}

/** Central error copy from {@link getErrorMessage}. */
export function getPairMatchingApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Central error copy from {@link getErrorMessage}. */
export function getVerifyErrorMessage(error: unknown): string {
  return getErrorMessage(error);
}

// After verify, any affectable slot wins over the row flag.
export function resolveMatchingAssignability(
  verifyClicked: boolean,
  slotVerifyMap: Map<string, SlotVerifyResult>,
  rowAffectability: boolean | null | undefined,
) {
  if (!verifyClicked) return getAssignabilityConfig(rowAffectability);
  const hasAssignable = [...slotVerifyMap.values()].some((result) => result.affectable === true);
  return getAssignabilityConfig(hasAssignable);
}

/** Slot-verify DTO; `programId` is required on the type but unused server-side. */
export function buildCheckAvailabilityRequest(params: {
  isTutor: boolean;
  userId: string;
  programId: string;
  availability: AvailabilitySlot[];
}): CheckAvailabilityRequest {
  return {
    tutor: params.isTutor,
    user: {
      userId: params.userId,
      programId: params.programId,
      availability: params.availability.map(toApiSlot),
    },
  };
}

/** Session-create prefill from the dashboard row + selected slot / candidate. */
export function buildMatchingSessionCreatePrefill(params: {
  perspective: MatchingPerspective;
  data: MatchingDashboardItem;
  addedCandidateId?: string;
  addedCandidateName?: string;
  addedCandidateProgramId?: string;
  selectedSlot: AvailabilitySlot | null;
}) {
  const isTutor = params.perspective === 'tutor';
  return {
    initialTutorId: isTutor ? params.data.userId : params.addedCandidateId,
    initialTutorLabel: isTutor ? params.data.fullName : params.addedCandidateName,
    initialStudentId: isTutor ? params.addedCandidateId : params.data.userId,
    initialStudentLabel: isTutor ? params.addedCandidateName : params.data.fullName,
    initialSessionDate: params.selectedSlot
      ? getNextWeekdayDate(params.selectedSlot.dayOfTheWeek)
      : undefined,
    initialStartTime: params.selectedSlot?.startTime,
    initialEndTime: params.selectedSlot?.endTime,
    initialProgramId: getInitialSessionProgramId(
      params.perspective,
      params.addedCandidateProgramId,
      params.data,
    ),
  };
}

/** Tutor perspective: first program of the added student (session create). */
export function resolveAddedCandidateProgramId(
  perspective: MatchingPerspective,
  candidateId: string,
  candidates: MatchingCandidate[],
): string | undefined {
  if (perspective !== 'tutor') return undefined;
  const candidate = candidates.find(
    (item) => 'studentUserId' in item && item.studentUserId === candidateId,
  );
  if (candidate && 'programs' in candidate) return candidate.programs[0]?.programId;
  return undefined;
}

/** Intersection of tutor levels with the student's `level` (id or name). */
export function getCommonLevelsForStudent(
  tutorLevels: UnmatchedTutorItem['levels'],
  student: StudentDashboardItem,
): UnmatchedTutorItem['levels'] {
  const studentLevelIds = new Set((student.level ?? []).map((level) => level.levelId));
  const studentLevelNames = new Set(
    (student.level ?? [])
      .map((level) => level.levelName?.toLowerCase())
      .filter((name): name is string => Boolean(name)),
  );

  return tutorLevels.filter((level) => {
    if (studentLevelIds.has(level.levelId)) return true;
    if (level.levelName != null) return studentLevelNames.has(level.levelName.toLowerCase());
    return studentLevelNames.has(level.levelId.toLowerCase());
  });
}

/** Intersection of tutor programs with the student's programs. */
export function getCommonProgramsForStudent(
  tutorPrograms: UnmatchedTutorItem['programs'],
  student: StudentDashboardItem,
): UnmatchedTutorItem['programs'] {
  const studentProgramIds = new Set((student.programs ?? []).map((program) => program.programId));
  return tutorPrograms.filter((program) => studentProgramIds.has(program.programId));
}

/** Slot selected and drop zone still empty (one candidate max). */
export function canAddCandidate(
  selectedSlot: AvailabilitySlot | null,
  addedCount: number,
): boolean {
  return !!selectedSlot && addedCount < 1;
}

/** Confirm needs a slot + one candidate; tutors cannot exceed remaining seats or add more than one. */
export function isConfirmPairDisabled(
  selectedSlot: AvailabilitySlot | null,
  addedCount: number,
  perspective: MatchingPerspective,
  data: MatchingDashboardItem,
): boolean {
  if (!selectedSlot || addedCount === 0) return true;
  if (perspective === 'student' && data.matchingStatus === 'matched') return true;
  if (perspective === 'tutor' && addedCount > 1) return true;
  if (perspective === 'tutor' && 'leftNumberOfStudentsToSupport' in data) {
    if (addedCount > data.leftNumberOfStudentsToSupport) return true;
    return data.leftNumberOfStudentsToSupport === 0;
  }
  return false;
}

/** Matched-partners query; `tutorAvailability` vs `studentAvailability`. */
export function buildMatchedQueryParams(
  data: MatchingDashboardItem,
  perspective: MatchingPerspective,
  selectedSlot: AvailabilitySlot | null,
) {
  if (!selectedSlot) return null;
  if (perspective === 'tutor') {
    return { userId: data.userId, tutorAvailability: JSON.stringify(selectedSlot) };
  }
  return { userId: data.userId, studentAvailability: JSON.stringify(selectedSlot) };
}

/** Unmatched-candidates query; name search key follows the opposite side. */
export function buildCandidatesQueryParams(
  data: MatchingDashboardItem,
  perspective: MatchingPerspective,
  selectedSlot: AvailabilitySlot | null,
  page: number,
  pageSize: number,
  searchQuery: string,
) {
  if (!selectedSlot) return null;
  if (perspective === 'tutor') {
    return {
      userId: data.userId,
      page,
      pageSize,
      tutorAvailability: JSON.stringify(selectedSlot),
      studentName: searchQuery || undefined,
    };
  }
  return {
    userId: data.userId,
    page,
    pageSize,
    studentAvailability: JSON.stringify(selectedSlot),
    tutorName: searchQuery || undefined,
  };
}

/** Tutor matching-dashboard query params (optional page / exportCsv). */
export function buildTutorDashboardQueryParams(
  filters: TutorFilters,
  options: MatchingDashboardQueryOptions,
): TutorDashboardParams {
  const params: TutorDashboardParams = {
    status: options.matchingStatus as never,
    tutor: filters.search,
    programs: filters.programIds,
    valorisation: filters.valorisation,
    cjValidatedFrom: filters.cjValidatedFrom,
    cjValidatedTo: filters.cjValidatedTo,
    participationStudies: filters.participationStudies,
    subject: filters.subject,
    participationRI: filters.participationRI,
    registrationDateFrom: filters.registrationDateFrom,
    registrationDateTo: filters.registrationDateTo,
    gender: filters.gender,
    numberOfStudentsToSupport: filters.minCapacity,
    level: filters.levelIds,
    affectability: options.assignability,
  };
  if (options.page !== undefined) params.page = options.page;
  if (options.pageSize !== undefined) params.pageSize = options.pageSize;
  if (options.exportCsv === true) params.exportCsv = true;
  return params;
}

/** Student matching-dashboard query params (optional page / exportCsv). */
export function buildStudentDashboardQueryParams(
  filters: StudentFilters,
  options: MatchingDashboardQueryOptions,
): StudentDashboardParams {
  const params: StudentDashboardParams = {
    status: options.matchingStatus as never,
    student: filters.search,
    program: filters.programId,
    subject: filters.subject,
    establishment: filters.establishmentId,
    partner: filters.partnerId ? true : undefined,
    partnerId: filters.partnerId,
    gender: filters.gender as never,
    level: filters.levelId,
    linkedUserId: filters.parentId,
    affectability: options.assignability,
  };
  if (options.page !== undefined) params.page = options.page;
  if (options.pageSize !== undefined) params.pageSize = options.pageSize;
  if (options.exportCsv === true) params.exportCsv = true;
  return params;
}

/** Tutor: added student's program; student: first program on the row. */
function getInitialSessionProgramId(
  perspective: MatchingPerspective,
  addedCandidateProgramId: string | undefined,
  data: MatchingDashboardItem,
): string | undefined {
  if (perspective === 'tutor') return addedCandidateProgramId;
  return data.programs[0]?.programId;
}

/** Hide the source card while dragging; dim when the drop zone is full. */
export function getCandidateCardOpacity(isDragging: boolean, isDisabled: boolean): number {
  if (isDragging) return 0;
  if (isDisabled) return 0.5;
  return 1;
}

/** Selected / taken / available / non-affectable after verify. */
export function getSlotVisualState(
  slot: AvailabilitySlot,
  selectedSlot: AvailabilitySlot | null,
  verifyClicked: boolean,
  slotVerifyMap: Map<string, SlotVerifyResult>,
) {
  const verifyResult = slotVerifyMap.get(slotApiKey(slot));
  return {
    isSelected: selectedSlot?.fullName === slot.fullName,
    isTaken: verifyClicked && verifyResult?.taken === true,
    isAvailable:
      verifyClicked && verifyResult?.taken === false && verifyResult?.affectable === true,
    isNonAffectable:
      verifyClicked && verifyResult?.taken === false && verifyResult?.affectable === false,
  };
}

/** Slot-pill tooltip when taken or non-affectable. */
export function getSlotDisabledTooltip(state: {
  isTaken: boolean;
  isNonAffectable: boolean;
}): string | null {
  if (state.isTaken) return 'Pris';
  if (state.isNonAffectable) return ASSIGNABILITY_CONFIG.false.label;
  return null;
}

/** Slot-pill class from taken / non-affectable / selected state. */
export function getSlotPillClassName(state: {
  isTaken: boolean;
  isNonAffectable: boolean;
  isSelected: boolean;
}): string {
  if (state.isTaken) return MATCHING_SLOT_PILL_CLASS.taken;
  if (state.isNonAffectable) return MATCHING_SLOT_PILL_CLASS.nonAffectable;
  if (state.isSelected) return MATCHING_SLOT_PILL_CLASS.selected;
  return MATCHING_SLOT_PILL_CLASS.default;
}

/** Slot-pill inline style from selected / taken / available / non-affectable. */
export function getSlotPillStyle(state: {
  isSelected: boolean;
  isTaken: boolean;
  isAvailable: boolean;
  isNonAffectable: boolean;
}): CSSProperties {
  if (state.isSelected) return MATCHING_SLOT_PILL_STYLE.selected;
  if (state.isTaken) return MATCHING_SLOT_PILL_STYLE.taken;
  if (state.isAvailable) return MATCHING_SLOT_PILL_STYLE.available;
  if (state.isNonAffectable) return MATCHING_SLOT_PILL_STYLE.nonAffectable;
  return MATCHING_SLOT_PILL_STYLE.default;
}

/** Quote and escape a CSV cell. */
function escapeCsv(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

/** Tutor `levels` vs student `level` for the CSV level column. */
function formatCsvLevels(row: MatchingDashboardItem): string {
  if ('levels' in row && row.levels) {
    return row.levels.map((level) => level.levelName ?? level.levelId).join(', ');
  }
  if ('level' in row && row.level) {
    return row.level.map((level) => level.levelName ?? level.levelId).join(', ');
  }
  return '';
}

/** Column order matches {@link MATCHING_CSV_HEADERS_TUTOR} / {@link MATCHING_CSV_HEADERS_STUDENT}. */
function buildMatchingCsvRow(
  row: MatchingDashboardItem,
  perspective: MatchingPerspective,
): string[] {
  const program = row.programs.map((item) => item.programName ?? item.programId).join('; ');
  const gender = getMatchingGenderConfig(row.gender, perspective).label;
  const subject = getDashboardRawSubject(row) ?? '';
  const level = formatCsvLevels(row);
  const partner = row.partnerName ?? '';
  const status = MATCHING_STATUS_CONFIG[row.matchingStatus]?.label ?? row.matchingStatus;
  const assignability = row.affectability
    ? ASSIGNABILITY_CONFIG.true.label
    : ASSIGNABILITY_CONFIG.false.label;
  const valorisation = getTutorValorisationBadge(getTutorDashboardValorisation(row)).label;

  if (perspective === 'tutor') {
    return [
      row.fullName,
      program,
      gender,
      subject,
      level,
      getMatchingCapacityLabel(row, perspective),
      status,
      assignability,
      valorisation,
    ];
  }
  return [row.fullName, program, gender, subject, level, status, assignability, partner];
}

/** UTF-8 BOM so Excel keeps accents. */
export function downloadAsCsv(
  rows: MatchingDashboardItem[],
  perspective: MatchingPerspective,
): void {
  const headers =
    perspective === 'tutor' ? MATCHING_CSV_HEADERS_TUTOR : MATCHING_CSV_HEADERS_STUDENT;
  const lines = rows.map((row) =>
    buildMatchingCsvRow(row, perspective)
      .map((value) => escapeCsv(String(value ?? '')))
      .join(','),
  );
  const csvContent = [headers.map((header) => escapeCsv(header)).join(','), ...lines].join('\r\n');
  const blob = new Blob([`\uFEFF${csvContent}`], { type: CSV_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `matching-${perspective}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** Fetch the full dashboard with `exportCsv: true` then download. */
export async function exportMatchingDashboardCsv(params: {
  companyId: string;
  perspective: MatchingPerspective;
  matchingStatus: string | undefined;
  assignability: boolean | undefined;
  tutorFilters: TutorFilters;
  studentFilters: StudentFilters;
}): Promise<void> {
  const { companyId, matchingStatus, assignability, tutorFilters, studentFilters } = params;
  if (params.perspective === 'tutor') {
    const result = await matchingService.getTutorDashboard(
      companyId,
      buildTutorDashboardQueryParams(tutorFilters, {
        matchingStatus,
        assignability,
        exportCsv: true,
      }),
    );
    downloadAsCsv(result.data, 'tutor');
    return;
  }
  const result = await matchingService.getStudentDashboard(
    companyId,
    buildStudentDashboardQueryParams(studentFilters, {
      matchingStatus,
      assignability,
      exportCsv: true,
    }),
  );
  downloadAsCsv(result.data, 'student');
}

/** {@link DataTable} rows use `userId` as `id`. */
export function toMatchingDashboardRows(
  items: MatchingDashboardItem[] | undefined,
): MatchingDashboardRow[] {
  return (items ?? []).map((item) => ({ ...item, id: item.userId }));
}

/** React Query keys for remote matching expanded-row candidates. */
export const matchingCandidatesKeys = {
  all: ['matching', 'candidates'] as const,
  tutorMatched: (companyId: string, params: TutorMatchedStudentsParams) =>
    [...matchingCandidatesKeys.all, 'tutor', 'matched', companyId, params] as const,
  tutorUnmatched: (companyId: string, params: TutorUnmatchedStudentsParams) =>
    [...matchingCandidatesKeys.all, 'tutor', 'unmatched', companyId, params] as const,
  studentMatched: (companyId: string, params: StudentMatchedTutorParams) =>
    [...matchingCandidatesKeys.all, 'student', 'matched', companyId, params] as const,
  studentUnmatched: (companyId: string, params: StudentUnmatchedTutorsParams) =>
    [...matchingCandidatesKeys.all, 'student', 'unmatched', companyId, params] as const,
};

/** `GET` students already matched to this tutor (expanded row col. 2). */
export function useTutorStudentsMatched(
  params: TutorMatchedStudentsParams | null,
  options?: { enabled?: boolean },
): UseQueryResult<MatchedStudentItem[]> {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: matchingCandidatesKeys.tutorMatched(companyId!, params!),
    queryFn: () => matchingService.getTutorStudentsMatched(companyId!, params!),
    enabled: !!companyId && !!params && options?.enabled !== false,
  });
}

/** `GET` unmatched compatible students for this tutor (expanded row col. 3). */
export function useTutorStudentsUnmatched(
  params: TutorUnmatchedStudentsParams | null,
  options?: { enabled?: boolean },
): UseQueryResult<DashboardResponse<UnmatchedStudentItem>> {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: matchingCandidatesKeys.tutorUnmatched(companyId!, params!),
    queryFn: () => matchingService.getTutorStudentsUnmatched(companyId!, params!),
    enabled: !!companyId && !!params && options?.enabled !== false,
  });
}

/** `GET` tutor already matched to this student (expanded row col. 2). */
export function useStudentTutorMatched(
  params: StudentMatchedTutorParams | null,
  options?: { enabled?: boolean },
): UseQueryResult<MatchedTutorItem[]> {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: matchingCandidatesKeys.studentMatched(companyId!, params!),
    queryFn: () => matchingService.getStudentTutorMatched(companyId!, params!),
    enabled: !!companyId && !!params && options?.enabled !== false,
  });
}

/** `GET` unmatched compatible tutors for this student (expanded row col. 3). */
export function useStudentTutorsUnmatched(
  params: StudentUnmatchedTutorsParams | null,
  options?: { enabled?: boolean },
): UseQueryResult<DashboardResponse<UnmatchedTutorItem>> {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: matchingCandidatesKeys.studentUnmatched(companyId!, params!),
    queryFn: () => matchingService.getStudentTutorsUnmatched(companyId!, params!),
    enabled: !!companyId && !!params && options?.enabled !== false,
  });
}

/** `POST /matching/check-availability` — `programId` is required on the DTO but unused server-side. */
export const useCheckAvailability = (): UseMutationResult<
  CheckAvailabilityResponse,
  Error,
  CheckAvailabilityRequest
> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useMutation({
    mutationFn: (body: CheckAvailabilityRequest) =>
      matchingService.checkAvailability(companyId!, body),
  });
};

/** Submit is allowed only when a different manager is selected. */
export function canSubmitManagerChange(selectedId: string, currentId?: string): boolean {
  return Boolean(selectedId) && selectedId !== currentId;
}

/** Prepend the current manager when they are missing from the fetched options. */
export function buildManagerSelectOptions(
  current: { userId: string; fullName?: string | null } | null | undefined,
  options: Array<{ value: string; label: string }>,
): Array<{ value: string; label: string }> {
  if (!current || options.some((opt) => opt.value === current.userId)) return options;
  return [{ value: current.userId, label: current.fullName ?? current.userId }, ...options];
}

/** Initial {@link Select} value; empty when the pair has no manager. */
export function initialManagerUserId(currentUserId?: string | null): string {
  return currentUserId ?? '';
}

/** PATCH payload for a manager reassignment. */
export function buildUpdatePairManagerDto(selectedManagerId: string): UpdatePairManagerDto {
  return { newManagerUserId: selectedManagerId };
}

/** Whether the selected reason requires a free-text description. */
export function dissociationRequiresDescription(reason: string): boolean {
  return DISSOCIATION_REASONS.find((item) => item.code === reason)?.requiresDescription || false;
}

/** Impact line for upcoming sessions that will be cancelled. */
export function formatFutureSessionsImpact(count: number): string {
  const plural = count === 1 ? '' : 's';
  return `${count} séance${plural} future${plural} impactée${plural}`;
}

/** Dissociation DTO; blank description is omitted. */
export function buildDissociatePairDto(
  reason: DissociationReasonCode,
  description: string,
): DissociatePairDto {
  return {
    reason,
    description: description.trim() || undefined,
  };
}
